import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, toastError } from "../../lib/api";
import { PageHeader, EmptyState } from "../../components/domain/Bits";
import { Settings2, ArrowRight, Clock3 } from "lucide-react";
import { toast } from "sonner";
import { useEffectiveRole } from "../../lib/roles";

export default function ConfigViewer() {
  const [cfg, setCfg] = useState(null);
  const [timezone, setTimezone] = useState("UTC");
  const [savedTimezone, setSavedTimezone] = useState("UTC");
  const [savingTimezone, setSavingTimezone] = useState(false);
  const role = useEffectiveRole();
  // Milestone F1 corrections §1 — surface the Relationship Types editor
  // to roles that legitimately administer or supervise configuration.
  // Representative + Viewer never see an administrative link.
  const showRelationshipTypesLink =
    role === "owner_admin" || role === "manager_trainer";
  const canEditRelationshipTypes = role === "owner_admin";
  const canEditTimezone = role === "owner_admin";

  useEffect(() => {
    (async () => {
      try {
        const [{ data: config }, { data: tenantSettings }] = await Promise.all([
          api.get("/config/viewer"),
          api.get("/tenant/settings"),
        ]);
        setCfg(config);
        const activeTimezone = tenantSettings.default_timezone || "UTC";
        setTimezone(activeTimezone);
        setSavedTimezone(activeTimezone);
      } catch (e) { toastError(e); }
    })();
  }, []);

  async function saveTimezone() {
    if (!canEditTimezone || timezone === savedTimezone) return;
    const approved = window.confirm(
      `Change the business timezone from ${savedTimezone} to ${timezone}? Existing stored timestamps will not be rewritten.`,
    );
    if (!approved) return;
    setSavingTimezone(true);
    try {
      await api.patch("/tenant/settings", { default_timezone: timezone });
      setSavedTimezone(timezone);
      toast.success("Business timezone updated");
    } catch (e) {
      setTimezone(savedTimezone);
      toastError(e);
    } finally {
      setSavingTimezone(false);
    }
  }

  if (!cfg) return (
    <>
      <PageHeader title="Configuration" subtitle="Read-only viewer for tenant configuration" />
      <EmptyState icon={Settings2} title="Loading…" />
    </>
  );

  const Section = ({ label, rows, cols }) => (
    <section className="border border-slate-200 rounded-lg bg-white overflow-hidden">
      <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-widest text-slate-600 font-semibold">
        {label} <span className="ml-2 text-slate-400 font-normal normal-case">({rows?.length || 0})</span>
      </div>
      {(!rows || rows.length === 0) ? (
        <div className="p-4 text-sm text-slate-400 italic">No entries</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-widest text-slate-500">
            <tr>{cols.map((c) => <th key={c} className="text-left px-4 py-2 font-medium">{c}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100">
                {cols.map((c) => <td key={c} className="px-4 py-2 text-slate-700"><span className="font-mono text-xs">{r[c] ?? "—"}</span></td>)}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );

  return (
    <>
      <PageHeader title="Configuration" subtitle={`Applied template: ${cfg.applied_template || "—"} v${cfg.applied_template_version || "—"} · Read-only`} />
      <div className="max-w-7xl mx-auto p-6 md:p-8 grid gap-4">
        <section className="border border-slate-200 rounded-lg bg-white p-4" data-testid="business-timezone-card">
          <div className="flex items-start gap-3">
            <Clock3 className="h-5 w-5 text-slate-500 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-800">Business Timezone</div>
              <p className="mt-1 text-xs text-slate-500">
                Calendar, tasks, My Day, reports, and date filters use this timezone. Historical UTC timestamps are never rewritten.
              </p>
              <div className="mt-3 flex flex-col sm:flex-row gap-2 sm:items-end">
                <label className="text-xs font-medium text-slate-600 flex-1">
                  Active timezone
                  <select
                    value={timezone}
                    onChange={(event) => setTimezone(event.target.value)}
                    disabled={!canEditTimezone || savingTimezone}
                    data-testid="business-timezone-select"
                    className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 disabled:bg-slate-50"
                  >
                    <option value="America/Vancouver">British Columbia — Pacific Time (America/Vancouver)</option>
                    <option value="America/Edmonton">Edmonton / Alberta — Mountain Time (America/Edmonton)</option>
                    <option value="Asia/Manila">Philippines — Philippine Time (Asia/Manila)</option>
                    <option value="UTC">UTC</option>
                  </select>
                </label>
                {canEditTimezone ? (
                  <button
                    type="button"
                    onClick={saveTimezone}
                    disabled={savingTimezone || timezone === savedTimezone}
                    data-testid="business-timezone-save"
                    className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {savingTimezone ? "Saving…" : "Save timezone"}
                  </button>
                ) : (
                  <span className="text-xs text-slate-500" data-testid="business-timezone-read-only">
                    Only a Tenant Owner can change this setting.
                  </span>
                )}
              </div>
              <div className="mt-2 text-xs text-slate-500" data-testid="business-timezone-current">
                Current: {savedTimezone}
              </div>
            </div>
          </div>
        </section>
        {showRelationshipTypesLink && (
          <Link
            to="/config/workflow"
            data-testid="config-workflow-editor-link"
            className="group border border-slate-200 rounded-lg bg-white p-4 flex items-center justify-between hover:border-slate-400 transition"
          >
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                Configuration
              </div>
              <div className="mt-1 text-sm font-medium text-slate-800">
                Terminology &amp; Workflow
              </div>
              <div className="mt-0.5 text-xs text-slate-500">
                {canEditRelationshipTypes
                  ? "Customize visible labels, stage guidance, outcomes, and required fields without changing protected system codes."
                  : "Read-only view of tenant terminology and workflow rules."}
              </div>
            </div>
            <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-slate-700 transition" />
          </Link>
        )}
        {showRelationshipTypesLink && (
          <Link
            to="/config/relationship-types"
            data-testid="config-relationship-types-link"
            className="group border border-slate-200 rounded-lg bg-white p-4 flex items-center justify-between hover:border-slate-400 transition"
          >
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                Configuration
              </div>
              <div className="mt-1 text-sm font-medium text-slate-800">
                Relationship Types
              </div>
              <div className="mt-0.5 text-xs text-slate-500">
                {canEditRelationshipTypes
                  ? "Create, edit, activate, reorder, and archive Contact-Organization relationship types."
                  : "Read-only view of tenant-owned Contact-Organization relationship types."}
              </div>
            </div>
            <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-slate-700 transition" />
          </Link>
        )}
        <div className="border border-slate-200 rounded-lg bg-white p-4">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Canonical stages</div>
          <div className="flex flex-wrap gap-2 font-mono text-xs">
            {cfg.canonical_stages.map((s) => (
              <span key={s} className="px-2 py-0.5 rounded border border-slate-200 bg-slate-50" data-testid={`canonical-${s}`}>{s}</span>
            ))}
          </div>
        </div>
        <Section label="Pipelines" rows={cfg.pipelines} cols={["code", "label", "is_default"]} />
        <Section label="Stages" rows={cfg.stages} cols={["code", "label", "order", "canonical_stage", "probability"]} />
        <Section label="Lifecycle statuses" rows={cfg.lifecycle_statuses} cols={["code", "label"]} />
        <Section label="Lead dispositions" rows={cfg.lead_dispositions} cols={["code", "label"]} />
        <Section label="Client dispositions" rows={cfg.client_dispositions} cols={["code", "label"]} />
        <Section label="Lead sources" rows={cfg.lead_sources} cols={["code", "label"]} />
        <Section label="Lost reasons" rows={cfg.lost_reasons} cols={["code", "label"]} />
        <Section label="Products / Services" rows={cfg.products_services} cols={["code", "label", "price", "currency"]} />
      </div>
    </>
  );
}
